// FILE: src/components/JourneyRoadmap.js
//
// 7-node Journey Roadmap. Pulls state from journeyMapService.deriveJourneyState
// and renders the corresponding roadmap_*.png asset for each node.
//
// On narrow screens it becomes a horizontal scroll-snap row so the seven
// tiles don't crush together — and centers the current node on mount so
// learners land on "where they are."

import React, { useEffect, useRef, useState } from 'react';
import DashCard from '../dashboard/DashCard';
import ImageLightbox from '../shared/ImageLightbox';
import styles from './JourneyRoadmap.module.css';
import { deriveJourneyState } from '../../services/journeyMapService';

const ASSET_BASE = process.env.PUBLIC_URL + '/themes/pm-pixel/Journey/';

export default function JourneyRoadmap({
  readiness,
  sessions,
  baselineAt,
  earnedByKey,
  onViewMilestones,
}) {
  const nodes = deriveJourneyState({ readiness, sessions, baselineAt, earnedByKey });
  const railRef = useRef(null);
  const [zoomNode, setZoomNode] = useState(null);

  // On narrow viewports, center the current node so learners land on
  // "you are here" rather than always at Baseline on the far left.
  useEffect(() => {
    if (typeof window === 'undefined' || !railRef.current) return;
    if (!window.matchMedia || !window.matchMedia('(max-width: 1024px)').matches) return;
    const cur = railRef.current.querySelector('[data-state="current"]');
    if (!cur) return;
    const target = cur.offsetLeft - (railRef.current.clientWidth / 2) + (cur.offsetWidth / 2);
    railRef.current.scrollLeft = Math.max(0, target);
  }, []);

  const coachLine = pickCoachLine(nodes);

  return (
    <DashCard
      eyebrow="Your Journey"
      action={
        <button type="button" onClick={onViewMilestones} data-testid="dashboard-view-milestones">
          View milestones ›
        </button>
      }
      testId="dashboard-journey-roadmap"
    >
      <div ref={railRef} className={styles.rail}>
        {nodes.map((n, idx) => (
          <div
            key={n.key}
            className={[
              styles.node,
              n.state === 'earned'   ? styles.earned : '',
              n.state === 'current'  ? styles.current : '',
              n.state === 'upcoming' ? styles.upcoming : '',
            ].filter(Boolean).join(' ')}
            data-state={n.state}
          >
            <button
              type="button"
              className={styles.box}
              onClick={() => setZoomNode(n)}
              aria-label={`View ${n.label} icon larger`}
              data-testid={`journey-node-zoom-${n.key}`}
            >
              <img src={ASSET_BASE + n.asset} alt="" />
            </button>
            <div className={styles.label}>{n.label}</div>
            <div className={styles.state}>{n.subtitle}</div>
            {idx < nodes.length - 1 && <div className={styles.connector} aria-hidden />}
          </div>
        ))}
      </div>
      <ImageLightbox
        item={zoomNode ? {
          src: ASSET_BASE + zoomNode.asset,
          title: zoomNode.label,
          subtitle: zoomNode.subtitle,
          chip: zoomNode.state === 'earned' ? 'Done' : zoomNode.state === 'current' ? 'Current step' : 'Upcoming',
          chipTone: zoomNode.state === 'earned' ? 'good' : zoomNode.state === 'current' ? 'active' : undefined,
        } : null}
        onClose={() => setZoomNode(null)}
      />
      <div className={styles.foot}>
        <span className={styles.coachLine}><span className={styles.star}>★</span> {coachLine}</span>
        <button type="button" className={styles.footLink} onClick={onViewMilestones}>
          View milestones ›
        </button>
      </div>
    </DashCard>
  );
}

function pickCoachLine(nodes) {
  const current = nodes.find((n) => n.state === 'current');
  const earnedCount = nodes.filter((n) => n.state === 'earned').length;
  if (!current) return 'Every step earned. Time to lock in the cert.';
  if (current.key === 'baseline') return 'Set your baseline — that calibrates the whole path.';
  if (current.key === 'practice') return 'Build a rhythm. A few sessions and the picture clears up fast.';
  if (current.key === 'targeted') return 'Targeted work on your weak phases is where readiness really moves.';
  if (current.key === 'full_sim') return 'A full simulation puts the pieces together — go for it.';
  if (current.key === 'ready')   return `You're at ${earnedCount}/7 — the 75+ mark is in reach.`;
  if (current.key === 'three_sims') return 'Three sims locks in the stamina for exam day.';
  if (current.key === 'certified') return 'Record the win whenever you take the exam.';
  return 'You\'re on the right path.';
}
